import React from 'react';
import { Calendar, FileText } from 'lucide-react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';

const InvoiceDetails = ({ 
  invoiceNumber, 
  invoiceDate, 
  dueDate, 
  poNumber, 
  onChange 
}) => {
  // Calculate due date based on payment terms (default 30 days)
  const calculateDueDate = (startDate, days = 30) => {
    const date = new Date(startDate);
    date?.setDate(date?.getDate() + days);
    return date?.toISOString()?.split('T')?.[0];
  };

  const handleInvoiceDateChange = (e) => {
    const newDate = e?.target?.value;
    onChange('invoiceDate', newDate);
    
    // Auto-update due date if it's not set
    if (!dueDate) {
      onChange('dueDate', calculateDueDate(newDate));
    }
  };

  const handleAutoGenerateNumber = () => {
    const newNumber = `INV-${new Date()?.getFullYear()}-${(Math.floor(Math.random() * 9000) + 1000)}`;
    onChange('invoiceNumber', newNumber);
  };

  return (
    <div className="bg-white rounded-lg border p-6">
      <div className="flex items-center gap-2 mb-4">
        <FileText className="h-5 w-5 text-primary" />
        <h2 className="text-lg font-semibold text-foreground">Invoice Details</h2>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-4">
        {/* Invoice Number */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">
            Invoice Number <span className="text-destructive">*</span>
          </label>
          <div className="flex gap-2">
            <Input
              value={invoiceNumber}
              onChange={(e) => onChange('invoiceNumber', e?.target?.value)}
              placeholder="INV-2024-001"
              required
              className="flex-1"
            />
            <Button
              variant="outline"
              size="icon"
              onClick={handleAutoGenerateNumber}
              title="Auto-generate number"
              iconName="RefreshCw"
            />
          </div>
        </div>

        {/* Invoice Date */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">
            Invoice Date <span className="text-destructive">*</span>
          </label>
          <div className="relative">
            <Input
              type="date"
              value={invoiceDate}
              onChange={handleInvoiceDateChange}
              required
              className="w-full"
            />
          </div>
        </div>

        {/* Due Date */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">
            Due Date <span className="text-destructive">*</span>
          </label>
          <div className="relative">
            <Input
              type="date"
              value={dueDate}
              onChange={(e) => onChange('dueDate', e?.target?.value)}
              required
              className="w-full"
            />
          </div>
          <div className="flex gap-1">
            {[15, 30, 60, 90]?.map(days => (
              <Button
                key={days}
                variant="ghost"
                size="sm"
                className="text-xs px-2 h-6"
                onClick={() => onChange('dueDate', calculateDueDate(invoiceDate, days))}
              >
                +{days}d
              </Button>
            ))}
          </div>
        </div>

        {/* PO Number */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">
            PO Number (Optional)
          </label>
          <Input
            value={poNumber}
            onChange={(e) => onChange('poNumber', e?.target?.value)}
            placeholder="PO-2024-001"
          />
        </div>
      </div>
      {/* Quick Date Presets */}
      <div className="mt-4 pt-4 border-t">
        <div className="flex items-center gap-2 text-sm">
          <Calendar className="h-4 w-4 text-muted-foreground" />
          <span className="text-muted-foreground">Quick presets:</span>
          <Button
            variant="ghost"
            size="sm"
            className="text-xs h-6"
            onClick={() => {
              const today = new Date()?.toISOString()?.split('T')?.[0];
              onChange('invoiceDate', today);
              onChange('dueDate', calculateDueDate(today));
            }}
          >
            Today
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-xs h-6"
            onClick={() => {
              const endOfMonth = new Date();
              endOfMonth?.setMonth(endOfMonth?.getMonth() + 1, 0);
              onChange('dueDate', endOfMonth?.toISOString()?.split('T')?.[0]);
            }}
          >
            End of Month
          </Button>
        </div>
      </div>
    </div>
  );
};

export default InvoiceDetails;